import React, { useMemo, useState } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas }     from "@fortawesome/free-solid-svg-icons"
library.add(fas)

import { postRequest } from "@react/helpers/fetch"
import Header   from "./components/Header"
import KpiStrip from "./components/KpiStrip"
import GroupsTable from "./components/GroupsTable"
import SummaryPane from "./components/SummaryPane"
import HistoryTable from "./components/HistoryTable"

const buildInitialGroups = data => (data.groups || []).map(group => ({
  ...group,
  services: group.services.map(service => ({ ...service, selected: false, draftPct: service.prev_pct })),
}))

const App = ({ data, save_url, back_route }) => {
  const guaranteeEnabled = data.guarantee_enabled !== false
  const [groups, setGroups] = useState(() => buildInitialGroups(data))
  const [guaranteePct, setGuaranteePct] = useState(data.default_guarantee_pct ?? 5)
  const [submitting, setSubmitting] = useState(false)
  const [downPaymentDeductedCents, setDownPaymentDeductedCents] = useState(0)

  const updateService = (groupId, serviceId, patch) => {
    setGroups(prev => prev.map(group => {
      if (group.id !== groupId) return group
      return {
        ...group,
        services: group.services.map(service => {
          if (service.id !== serviceId) return service
          const next = { ...service, ...patch }
          if (patch.draftPct != null) {
            next.draftPct = Math.max(service.prev_pct, Math.min(100, Math.round(patch.draftPct)))
            if (!service.locked) next.selected = next.draftPct > service.prev_pct
          }
          return next
        }),
      }
    }))
  }

  const cascadeGroup = (groupId, pct) => {
    setGroups(prev => prev.map(group => {
      if (group.id !== groupId) return group
      return {
        ...group,
        services: group.services.map(service => {
          if (service.locked) return service
          const clamped = Math.max(service.prev_pct, Math.min(100, Math.round(pct)))
          return { ...service, draftPct: clamped, selected: clamped > service.prev_pct }
        }),
      }
    }))
  }

  const toggleService = (groupId, serviceId, checked) => updateService(groupId, serviceId, { selected: checked })

  const toggleGroup = (groupId, checked) => {
    setGroups(prev => prev.map(group => {
      if (group.id !== groupId) return group
      return { ...group, services: group.services.map(service => service.locked ? service : { ...service, selected: checked }) }
    }))
  }

  const toggleAll = checked => {
    setGroups(prev => prev.map(group => ({
      ...group,
      services: group.services.map(service => service.locked ? service : { ...service, selected: checked }),
    })))
  }

  // Pre-fills each line's cumulative with the project progress declared
  // on the tasks (without ever dropping below the cumulative already billed).
  const prefillFromProgress = () => {
    setGroups(prev => prev.map(group => ({
      ...group,
      services: group.services.map(service => {
        if (service.locked || service.task_pct == null) return service
        const next = Math.max(service.prev_pct, Math.min(100, Math.round(service.task_pct)))
        return { ...service, draftPct: next, selected: next > service.prev_pct }
      }),
    })))
  }

  const hasTaskProgress = useMemo(
    () => groups.some(group => group.services.some(service => service.task_pct != null)),
    [groups]
  )

  const selectedServices = useMemo(() => {
    const result = []
    groups.forEach(group => group.services.forEach(service => {
      if (!service.selected) return
      const cumulCents = Math.round((service.init_cents * service.draftPct) / 100)
      const toBillCents = Math.max(0, cumulCents - service.prev_cents)
      if (toBillCents > 0) result.push({ ...service, cumulCents, toBillCents })
    }))
    return result
  }, [groups])

  const submit = (mode = "selection") => {
    if (submitting) return
    let payload = selectedServices
    if (mode === "all") {
      payload = []
      groups.forEach(group => group.services.forEach(service => {
        if (service.locked) return
        const cumulCents = Math.round((service.init_cents * service.draftPct) / 100)
        const toBillCents = Math.max(0, cumulCents - service.prev_cents)
        if (toBillCents > 0) payload.push({ ...service, cumulCents, toBillCents })
      }))
    }

    if (payload.length === 0) {
      flashAlert(I18n.t("projects.situations.errors.no_lines"))
      return
    }

    setSubmitting(true)
    postRequest({
      url:  save_url,
      body: {
        guarantee_holdback_percentage: guaranteeEnabled ? guaranteePct : 0,
        down_payment_deducted_cents:   downPaymentDeductedCents,
        lines: payload.map(service => ({
          qline_id:     service.id,
          percentage:   service.draftPct,
          amount_cents: service.toBillCents,
        })),
      },
    })
      .then(response => {
        if (!response?.success) {
          flashAlert(response?.errors ? (Array.isArray(response.errors) ? response.errors.join(", ") : String(response.errors)) : I18n.t("quotes.builder.footer.unknown_error"))
          setSubmitting(false)
          return
        }
        if (response.redirect_to) {
          window.location.href = response.redirect_to
        } else {
          setSubmitting(false)
        }
      })
      .catch(() => {
        flashAlert(I18n.t("quotes.builder.footer.network_error"))
        setSubmitting(false)
      })
  }

  return (
    <div className="font-sans bg-slate-50 min-h-screen text-slate-900">
      <Header project={data.project} quote={data.quote} backRoute={back_route} />

      <div className="px-8 py-6 space-y-6">
        <KpiStrip groups={groups} initialKpis={data.kpis} currencySymbol={data.project.currency_symbol} />

        <GroupsTable
          groups              = {groups}
          onServiceChange     = {updateService}
          onGroupCascade      = {cascadeGroup}
          onToggleService     = {toggleService}
          onToggleGroup       = {toggleGroup}
          onToggleAll         = {toggleAll}
          onPrefillFromProgress = {hasTaskProgress ? prefillFromProgress : null}
          currencySymbol      = {data.project.currency_symbol}
          nextSituationNumber = {data.next_situation_number}
        />

        <div className="grid grid-cols-2 gap-5 items-start">
          <SummaryPane
            selectedServices             = {selectedServices}
            nextSituationNumber          = {data.next_situation_number}
            guaranteeEnabled             = {guaranteeEnabled}
            guaranteePct                 = {guaranteePct}
            onGuaranteeChange            = {setGuaranteePct}
            bankGuarantee                = {data.bank_guarantee}
            downPayment                  = {data.down_payment}
            downPaymentDeductedCents     = {downPaymentDeductedCents}
            onDownPaymentDeductedChange  = {setDownPaymentDeductedCents}
            progressPct                  = {data.kpis?.global_progress_pct || 0}
            onBillSelection              = {() => submit("selection")}
            onBillAll                    = {() => submit("all")}
            submitting                   = {submitting}
            currencySymbol               = {data.project.currency_symbol}
          />
          <HistoryTable
            history               = {data.history}
            currencySymbol        = {data.project.currency_symbol}
            emailDefaultRecipient = {data.email_default_recipient}
            projectName           = {data.project.name}
          />
        </div>
      </div>
    </div>
  )
}

export default App
